import React, { useState, useMemo } from 'react';
import {
  Container,
  Grid,
  Button,
  Typography,
  CircularProgress,
  Box,
  Card,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField as MuiTextField,
  Chip,
  Stack,
  Avatar,
  Paper,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableContainer,
  TableRow,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  LinearProgress,
  Tooltip,
  useTheme,
  alpha,
} from '@mui/material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import moment from 'moment';
import Page from 'src/components/Page';
import Iconify from 'src/components/Iconify';
import { ToastNotifications } from 'src/components';
import { useAuth } from 'src/context/auth/AuthContext';
import { getRoleLabel } from 'src/config/permissions';
import { useHeaderBreadcrumbs } from 'src/context/header/HeaderContext';
import { loanService } from 'src/services/loan.service';
import { studentService } from 'src/services/student.service';
import { savingsAccountService } from 'src/services/savingsAccount.service';
import {
  calculateLoanRepayment,
  calculateStudentLoanEligibility,
  calculateLoanProgress,
  formatScholarDollars,
  AmortizationInstallment,
} from 'src/utils/loanCalculator';
import { invalidateStudentFinancialQueries } from 'src/utils/queryInvalidation';

const REJECTION_CATEGORIES = [
  { value: 'behavior', label: 'S.T.R.E.E.T. Cred / Behavior Concerns' },
  { value: 'credit_limit', label: 'Requested Amount Exceeds Grade Limit' },
  { value: 'cash_flow', label: 'Insufficient Cash Flow / High Existing Debt' },
  { value: 'purpose', label: 'Loan Purpose Needs Refinement' },
  { value: 'other', label: 'Other Educational Guidance' },
];

export default function ViewLoan() {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ loanId?: string; id?: string }>();
  const loanId = params.loanId || params.id;
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const isAdmin = location.pathname.startsWith('/admin');
  const baseRoute = isAdmin ? '/admin' : '/teacher';

  const [actionLoading, setActionLoading] = useState<boolean>(false);

  // Modals state
  const [isRejectModalOpen, setIsRejectModalOpen] = useState<boolean>(false);
  const [rejectReason, setRejectReason] = useState<string>('');
  const [rejectCategory, setRejectCategory] = useState<string>('behavior');

  const [isCounterOfferModalOpen, setIsCounterOfferModalOpen] = useState<boolean>(false);
  const [counterAmount, setCounterAmount] = useState<number>(30);
  const [counterTerm, setCounterTerm] = useState<number>(3);
  const [counterRate, setCounterRate] = useState<number>(5);
  const [counterNote, setCounterNote] = useState<string>('');

  const [isRecordPaymentModalOpen, setIsRecordPaymentModalOpen] = useState<boolean>(false);
  const [paymentAmount, setPaymentAmount] = useState<string>('');
  const [paymentNote, setPaymentNote] = useState<string>('');

  // 1. Fetch Loan Details
  const {
    data: loan,
    isLoading: isLoanLoading,
  } = useQuery({
    queryKey: ['loan-details', loanId],
    queryFn: () => loanService.getLoanById(loanId as string),
    enabled: !!loanId,
  });

  const studentId = loan?.studentid;

  // 2. Fetch Student Profile & Behavior
  const { data: student } = useQuery({
    queryKey: ['student-profile', studentId],
    queryFn: () => studentService.getStudentById(studentId as string),
    enabled: !!studentId,
  });

  const { data: behaviorCount } = useQuery({
    queryKey: ['student-behavior-count', studentId],
    queryFn: () => studentService.getStudentBehaviorCount(studentId as string),
    enabled: !!studentId,
  });

  const { data: savingsAccounts = [] } = useQuery({
    queryKey: ['student-savings-accounts', studentId],
    queryFn: () => savingsAccountService.getStudentSavingsAccounts(studentId as string),
    enabled: !!studentId,
  });

  const studentUser = loan?.student?.user;
  const fullName = studentUser ? `${studentUser.firstname} ${studentUser.lastname}` : 'Scholar';

  useHeaderBreadcrumbs({
    breadcrumbs: [
      { label: isAdmin ? 'School Lending & Credit' : 'Classroom Lending & Credit', path: `${baseRoute}/loan-requests` },
      { label: `Underwriting · ${fullName}` },
    ],
  });

  const totalSavings = useMemo(() => {
    return (savingsAccounts || [])
      .filter((a) => a.is_active)
      .reduce((sum, a) => sum + (Number(a.current_amount) || 0), 0);
  }, [savingsAccounts]);

  const netStreetCred = useMemo(() => {
    const pos = behaviorCount?.positive || 0;
    const neg = behaviorCount?.negative || 0;
    return pos - neg;
  }, [behaviorCount]);

  const eligibility = useMemo(() => {
    return calculateStudentLoanEligibility(
      student?.grade,
      netStreetCred,
      Number(student?.balance) || 0,
      1
    );
  }, [student, netStreetCred]);

  const loanProgress = useMemo(() => {
    return loan ? calculateLoanProgress(loan) : null;
  }, [loan]);

  // Set initial counter-offer state when loan is loaded
  React.useEffect(() => {
    if (loan) {
      setCounterAmount(Number(loan.amount) || 30);
      setCounterTerm(Number(loan.term_months) || 3);
      setCounterRate(Number(loan.interest_rate) || 5);
    }
  }, [loan]);

  // Counter offer live calculation
  const counterCalc = useMemo(() => {
    return calculateLoanRepayment(counterAmount, counterTerm, counterRate);
  }, [counterAmount, counterTerm, counterRate]);

  // Handle Approve
  const handleApproveLoan = async () => {
    if (!loanId) return;
    setActionLoading(true);
    try {
      await loanService.approveLoan(loanId, {
        approvedBy: {
          id: user?.id,
          name: `${user?.firstname || ''} ${user?.lastname || ''}`.trim() || 'Educator',
          role: getRoleLabel(user?.role) || 'Teacher',
        },
      });

      ToastNotifications.success('Loan approved and funds disbursed to student checking wallet!');

      await invalidateStudentFinancialQueries(queryClient, {
        extraKeys: [['loan-details'], ['loans'], ['teacher-loans'], ['student-loans']],
      });

      setTimeout(() => {
        navigate(-1);
      }, 600);
    } catch (err: any) {
      ToastNotifications.error(err?.message || 'Failed to approve loan.');
      console.error('[ViewLoan.handleApproveLoan]:', err);
    } finally {
      setActionLoading(false);
    }
  };

  // Handle Counter-Offer
  const handleCounterOffer = async () => {
    if (!loanId) return;
    setActionLoading(true);
    try {
      await loanService.counterOfferLoan(loanId, {
        amount: counterAmount,
        termMonths: counterTerm,
        interestRate: counterRate,
        note: counterNote.trim(),
        offeredBy: `${user?.firstname || ''} ${user?.lastname || ''}`.trim() || 'Educator',
      });

      ToastNotifications.success('Counter-offer terms saved and updated.');
      setIsCounterOfferModalOpen(false);

      await invalidateStudentFinancialQueries(queryClient, {
        extraKeys: [['loan-details'], ['loans'], ['teacher-loans']],
      });
    } catch (err: any) {
      ToastNotifications.error(err?.message || 'Failed to counter-offer loan.');
    } finally {
      setActionLoading(false);
    }
  };

  // Handle Reject
  const handleRejectLoan = async () => {
    if (!loanId || !rejectReason.trim()) {
      ToastNotifications.error('Please provide a reason for declining.');
      return;
    }
    setActionLoading(true);
    try {
      await loanService.rejectLoan(loanId, {
        reason: rejectReason.trim(),
        category: rejectCategory,
      });

      ToastNotifications.success('Loan application declined with educational feedback.');
      setIsRejectModalOpen(false);

      await invalidateStudentFinancialQueries(queryClient, {
        extraKeys: [['loan-details'], ['loans'], ['teacher-loans']],
      });

      setTimeout(() => {
        navigate(-1);
      }, 600);
    } catch (err: any) {
      ToastNotifications.error(err?.message || 'Failed to decline loan.');
    } finally {
      setActionLoading(false);
    }
  };

  // Handle Educator Record Payment
  const handleRecordPayment = async () => {
    if (!loanId || !studentId) return;
    const amountNum = parseFloat(paymentAmount);
    if (isNaN(amountNum) || amountNum <= 0) {
      ToastNotifications.error('Please enter a valid payment amount.');
      return;
    }

    setActionLoading(true);
    try {
      await loanService.repayLoan({
        loanId,
        studentId,
        amount: amountNum,
        paymentMethod: 'manual_educator',
        note: paymentNote.trim() || 'Educator recorded payment',
      });

      ToastNotifications.success(`Recorded payment of ${formatScholarDollars(amountNum)} successfully!`);
      setIsRecordPaymentModalOpen(false);
      setPaymentAmount('');
      setPaymentNote('');

      await invalidateStudentFinancialQueries(queryClient, {
        extraKeys: [['loan-details'], ['loans'], ['teacher-loans']],
      });
    } catch (err: any) {
      ToastNotifications.error(err?.message || 'Failed to record payment.');
    } finally {
      setActionLoading(false);
    }
  };

  if (isLoanLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="500px">
        <CircularProgress size={36} />
      </Box>
    );
  }

  const isPending = loan?.status === 'Pending';
  const isApproved = loan?.status === 'Approved' || loan?.status === 'Overdue';
  const isPaid = loan?.status === 'Paid';
  const isDenied = loan?.status === 'Denied';

  return (
    <Page title={`Loan Review · ${fullName}`}>
      <Container maxWidth={false} sx={{ py: 2.5, px: { xs: 2, md: 3 } }}>
        {/* Top Header Row with Icon Box */}
        <Box sx={{ mb: 2.5 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={1.5}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.primary.main, 0.12),
                  color: 'primary.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Iconify icon="solar:shield-check-bold-duotone" width={24} height={24} />
              </Box>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', letterSpacing: '-0.01em', fontSize: '1.375rem' }}>
                  Loan Underwriting & Decisioning Hub
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25, fontSize: '0.8125rem' }}>
                  Application ID: #{loan?.id} · Submitted {moment(loan?.createdat).format('MMMM D, YYYY [at] h:mm A')}
                </Typography>
              </Box>
            </Stack>

            <Button
              variant="outlined"
              size="small"
              startIcon={<Iconify icon="solar:arrow-left-linear" width={16} />}
              onClick={() => navigate(`${baseRoute}/loan-requests`)}
              sx={{
                borderColor: 'divider',
                color: 'text.primary',
                textTransform: 'none',
                borderRadius: 1.5,
                height: 36,
                fontWeight: 600,
                fontSize: '0.8125rem',
                '&:hover': { bgcolor: 'grey.100', borderColor: 'text.secondary' },
              }}
            >
              Back to Queue
            </Button>
          </Stack>
        </Box>

        {/* Full-Width Scholar Underwriting Profile & Decision Ribbon */}
        <Card
          elevation={0}
          sx={{
            p: 2.5,
            mb: 2.5,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <Grid container spacing={2.5} alignItems="center">
            {/* Scholar Identity */}
            <Grid item xs={12} md={4} lg={3.5}>
              <Stack direction="row" spacing={1.75} alignItems="center">
                <Avatar
                  src={studentUser?.photourl || undefined}
                  sx={{
                    width: 48,
                    height: 48,
                    bgcolor: 'primary.main',
                    fontSize: '1.15rem',
                    fontWeight: 700,
                  }}
                >
                  {studentUser?.firstname?.charAt(0) || 'S'}
                </Avatar>
                <Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '1.05rem' }}>
                      {fullName}
                    </Typography>
                    <Chip
                      label={`Grade ${loan?.student?.grade || '--'}`}
                      size="small"
                      sx={{ height: 20, fontSize: '0.6875rem', fontWeight: 700, bgcolor: 'grey.100', color: 'text.secondary' }}
                    />
                  </Stack>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8125rem' }}>
                    {studentUser?.email}
                  </Typography>
                  <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.5 }}>
                    <Chip
                      icon={<Iconify icon="solar:star-bold-duotone" width={12} />}
                      label={`${eligibility.creditTier} Borrower`}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.6875rem',
                        fontWeight: 700,
                        bgcolor: eligibility.meritDiscountApplied ? 'success.lighter' : 'warning.lighter',
                        color: eligibility.meritDiscountApplied ? 'success.main' : 'warning.main',
                      }}
                    />
                    <Chip
                      label={loan?.status}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.6875rem',
                        fontWeight: 700,
                        bgcolor: isApproved ? 'success.lighter' : isPending ? 'warning.lighter' : isPaid ? 'info.lighter' : 'error.lighter',
                        color: isApproved ? 'success.main' : isPending ? 'warning.main' : isPaid ? 'info.main' : 'error.main',
                      }}
                    />
                  </Stack>
                </Box>
              </Stack>
            </Grid>

            {/* 360° Financial Snapshot Strip */}
            <Grid item xs={12} md={5} lg={4.5}>
              <Grid container spacing={1}>
                <Grid item xs={4}>
                  <Box sx={{ p: 1, bgcolor: 'grey.50', borderRadius: 1.5, border: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '0.6875rem', textTransform: 'uppercase' }}>
                      Wallet Balance
                    </Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'success.main', mt: 0.25 }}>
                      {formatScholarDollars(student?.balance || 0)}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={4}>
                  <Box sx={{ p: 1, bgcolor: 'grey.50', borderRadius: 1.5, border: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '0.6875rem', textTransform: 'uppercase' }}>
                      Total Savings
                    </Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'info.main', mt: 0.25 }}>
                      {formatScholarDollars(totalSavings)}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={4}>
                  <Box sx={{ p: 1, bgcolor: 'grey.50', borderRadius: 1.5, border: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '0.6875rem', textTransform: 'uppercase' }}>
                      S.T.R.E.E.T. Cred
                    </Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: netStreetCred >= 0 ? 'success.main' : 'error.main', mt: 0.25 }}>
                      {netStreetCred > 0 ? `+${netStreetCred}` : netStreetCred} pts
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Grid>

            {/* Quick Underwriting Actions */}
            <Grid item xs={12} md={4} lg={4.5}>
              {isPending && (
                <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center" flexWrap="nowrap">
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    startIcon={<Iconify icon="solar:check-circle-bold-duotone" width={16} />}
                    onClick={handleApproveLoan}
                    disabled={actionLoading}
                    sx={{
                      height: 36,
                      borderRadius: 1.5,
                      textTransform: 'none',
                      fontWeight: 700,
                      fontSize: '0.8125rem',
                      px: 1.75,
                      whiteSpace: 'nowrap',
                      minWidth: 'max-content',
                    }}
                  >
                    {actionLoading ? <CircularProgress size={18} color="inherit" /> : 'Approve Loan'}
                  </Button>

                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Iconify icon="solar:pen-bold-duotone" width={16} />}
                    onClick={() => setIsCounterOfferModalOpen(true)}
                    disabled={actionLoading}
                    sx={{
                      height: 36,
                      borderColor: 'warning.main',
                      color: 'warning.main',
                      borderRadius: 1.5,
                      textTransform: 'none',
                      fontWeight: 700,
                      fontSize: '0.8125rem',
                      px: 1.75,
                      whiteSpace: 'nowrap',
                      minWidth: 'max-content',
                      '&:hover': { bgcolor: 'warning.lighter', borderColor: 'warning.main' },
                    }}
                  >
                    Counter-Offer
                  </Button>

                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    startIcon={<Iconify icon="solar:close-circle-bold-duotone" width={16} />}
                    onClick={() => setIsRejectModalOpen(true)}
                    disabled={actionLoading}
                    sx={{
                      height: 36,
                      borderRadius: 1.5,
                      textTransform: 'none',
                      fontWeight: 700,
                      fontSize: '0.8125rem',
                      px: 1.75,
                      whiteSpace: 'nowrap',
                      minWidth: 'max-content',
                    }}
                  >
                    Decline Loan
                  </Button>
                </Stack>
              )}

              {isApproved && (
                <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center" flexWrap="nowrap">
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    startIcon={<Iconify icon="solar:card-recive-bold-duotone" width={16} />}
                    onClick={() => setIsRecordPaymentModalOpen(true)}
                    sx={{
                      height: 36,
                      borderRadius: 1.5,
                      textTransform: 'none',
                      fontWeight: 700,
                      fontSize: '0.8125rem',
                      px: 2,
                      whiteSpace: 'nowrap',
                      minWidth: 'max-content',
                    }}
                  >
                    Record Payment / Deduction
                  </Button>
                </Stack>
              )}
            </Grid>
          </Grid>
        </Card>

        {/* Main Content: Left Column (Application Terms & Coaching) + Right Column (Schedule Table) */}
        <Grid container spacing={2.5}>
          {/* Left Column: Loan Terms & Purpose */}
          <Grid item xs={12} lg={4.5}>
            {/* Card 1: Loan Terms */}
            <Card
              elevation={0}
              sx={{
                p: 2.5,
                mb: 2.5,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', mb: 2, fontSize: '1rem' }}>
                Application Financial Terms
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '0.6875rem', textTransform: 'uppercase' }}>
                    Principal Amount
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main', mt: 0.25, fontSize: '1.25rem' }}>
                    {formatScholarDollars(loan?.amount)}
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '0.6875rem', textTransform: 'uppercase' }}>
                    Repayment Duration
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', mt: 0.25, fontSize: '1.25rem' }}>
                    {loan?.term_months} Months
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '0.6875rem', textTransform: 'uppercase' }}>
                    Annual Interest Rate
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: 'info.main', mt: 0.25, fontSize: '1.25rem' }}>
                    {loan?.interest_rate}% APR
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '0.6875rem', textTransform: 'uppercase' }}>
                    Monthly Installment
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main', mt: 0.25, fontSize: '1.25rem' }}>
                    {formatScholarDollars(loan?.monthly_payment)}
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '0.8125rem' }}>
                  Total Scheduled Repayment:
                </Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: 'text.primary', fontSize: '1.1rem' }}>
                  {formatScholarDollars(loan?.total_amount)}
                </Typography>
              </Stack>
            </Card>

            {/* Card 2: Loan Purpose & Feedback */}
            <Card
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', mb: 1.5, fontSize: '1rem' }}>
                Scholar Purpose Statement
              </Typography>

              <Paper elevation={0} sx={{ p: 1.75, bgcolor: 'grey.50', borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500, lineHeight: 1.6, fontSize: '0.875rem' }}>
                  "{loan?.reason}"
                </Typography>
              </Paper>

              {loan?.extendedproperties?.rejectionReason && (
                <Box sx={{ mt: 2, p: 1.5, bgcolor: 'error.lighter', borderRadius: 1.5, border: '1px solid', borderColor: alpha(theme.palette.error.main, 0.2) }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: 'error.main' }}>
                    Recorded Decline Feedback:
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'error.main', mt: 0.25, fontSize: '0.8125rem' }}>
                    {loan.extendedproperties.rejectionReason}
                  </Typography>
                </Box>
              )}

              {loan?.extendedproperties?.counterOffer && (
                <Box sx={{ mt: 2, p: 1.5, bgcolor: 'warning.lighter', borderRadius: 1.5, border: '1px solid', borderColor: alpha(theme.palette.warning.main, 0.2) }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: 'warning.main' }}>
                    Active Counter-Offer Terms:
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'warning.main', mt: 0.25, fontSize: '0.8125rem' }}>
                    {formatScholarDollars(loan.extendedproperties.counterOffer.amount)} over {loan.extendedproperties.counterOffer.termMonths} months @ {loan.extendedproperties.counterOffer.interestRate}% APR.
                  </Typography>
                  {loan.extendedproperties.counterOffer.note && (
                    <Typography variant="caption" sx={{ color: 'warning.main', display: 'block', mt: 0.5 }}>
                      Note: "{loan.extendedproperties.counterOffer.note}"
                    </Typography>
                  )}
                </Box>
              )}
            </Card>
          </Grid>

          {/* Right Column: Amortization Schedule & Repayment Progress */}
          <Grid item xs={12} lg={7.5}>
            <Card
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', mb: 2, fontSize: '1rem' }}>
                Amortization & Repayment Schedule
              </Typography>

              {/* Progress Bar for Active / Paid Loans */}
              {loanProgress && (isApproved || isPaid) && (
                <Box sx={{ p: 2, mb: 2.5, bgcolor: 'grey.50', borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
                  <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.75 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                      Repaid: {formatScholarDollars(loanProgress.paidAmount)} of {formatScholarDollars(loanProgress.totalAmount)}
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'success.main' }}>
                      Remaining Balance: {formatScholarDollars(loanProgress.remainingBalance)} ({loanProgress.percentPaid}% paid)
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={loanProgress.percentPaid}
                    sx={{
                      height: 7,
                      borderRadius: 3.5,
                      bgcolor: 'grey.200',
                      '& .MuiLinearProgress-bar': { bgcolor: 'success.main', borderRadius: 3.5 },
                    }}
                  />
                </Box>
              )}

              {/* Schedule Table */}
              <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
                <Table size="small">
                  <TableHead sx={{ bgcolor: 'grey.50' }}>
                    <TableRow>
                      <TableCell sx={{ py: 1.25, px: 1.5, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>#</TableCell>
                      <TableCell sx={{ py: 1.25, px: 1.5, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>Due Date</TableCell>
                      <TableCell sx={{ py: 1.25, px: 1.5, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>Installment</TableCell>
                      <TableCell sx={{ py: 1.25, px: 1.5, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>Principal</TableCell>
                      <TableCell sx={{ py: 1.25, px: 1.5, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>Interest</TableCell>
                      <TableCell sx={{ py: 1.25, px: 1.5, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>Remaining</TableCell>
                      <TableCell sx={{ py: 1.25, px: 1.5, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(loan?.extendedproperties?.amortizationSchedule || []).map((inst: AmortizationInstallment) => (
                      <TableRow key={inst.installmentNumber} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                        <TableCell sx={{ fontWeight: 700 }}>{inst.installmentNumber}</TableCell>
                        <TableCell>{moment(inst.dueDate).format('MMM D, YYYY')}</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: 'primary.main' }}>{formatScholarDollars(inst.amount)}</TableCell>
                        <TableCell>{formatScholarDollars(inst.principal)}</TableCell>
                        <TableCell sx={{ color: 'text.secondary' }}>{formatScholarDollars(inst.interest)}</TableCell>
                        <TableCell>{formatScholarDollars(inst.remainingBalance)}</TableCell>
                        <TableCell>
                          <Chip
                            icon={<Iconify icon={inst.status === 'paid' ? 'solar:check-circle-bold-duotone' : 'solar:hourglass-bold-duotone'} width={12} />}
                            label={inst.status === 'paid' ? 'Paid' : 'Pending'}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: '0.7rem',
                              fontWeight: 700,
                              bgcolor: inst.status === 'paid' ? 'success.lighter' : 'warning.lighter',
                              color: inst.status === 'paid' ? 'success.main' : 'warning.main',
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* 1. COUNTER OFFER MODAL */}
      <Dialog
        open={isCounterOfferModalOpen}
        onClose={() => !actionLoading && setIsCounterOfferModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          Counter-Offer Loan Terms
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2, fontSize: '0.8125rem' }}>
            Adjust the loan amount, repayment term, or interest rate. An explanatory note will guide the student.
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <MuiTextField
                fullWidth
                size="small"
                type="number"
                label="Amount ($D)"
                value={counterAmount}
                onChange={(e) => setCounterAmount(Number(e.target.value))}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <MuiTextField
                fullWidth
                size="small"
                type="number"
                label="Term (Months)"
                value={counterTerm}
                onChange={(e) => setCounterTerm(Number(e.target.value))}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <MuiTextField
                fullWidth
                size="small"
                type="number"
                label="Interest (% APR)"
                value={counterRate}
                onChange={(e) => setCounterRate(Number(e.target.value))}
              />
            </Grid>

            <Grid item xs={12}>
              <Paper elevation={0} sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                    New Monthly Installment:
                  </Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'primary.main' }}>
                    {formatScholarDollars(counterCalc.monthlyPayment)} / mo
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                    New Total Repayment:
                  </Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                    {formatScholarDollars(counterCalc.totalRepayment)}
                  </Typography>
                </Stack>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <MuiTextField
                fullWidth
                multiline
                rows={3}
                label="Coaching Note for Student"
                placeholder="Explain why these modified terms are better suited for their current goals..."
                value={counterNote}
                onChange={(e) => setCounterNote(e.target.value)}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setIsCounterOfferModalOpen(false)} sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1.5 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleCounterOffer}
            disabled={actionLoading || counterAmount <= 0}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 1.5 }}
          >
            {actionLoading ? <CircularProgress size={20} color="inherit" /> : 'Save Counter-Offer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 2. REJECTION / DECLINE MODAL */}
      <Dialog
        open={isRejectModalOpen}
        onClose={() => !actionLoading && setIsRejectModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: 'error.main' }}>
          Decline Loan Application
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2, fontSize: '0.8125rem' }}>
            Provide constructive educational feedback explaining why this loan is declined and how the student can improve eligibility.
          </Typography>

          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Coaching Reason Category</InputLabel>
            <Select
              value={rejectCategory}
              label="Coaching Reason Category"
              onChange={(e) => setRejectCategory(e.target.value)}
            >
              {REJECTION_CATEGORIES.map((cat) => (
                <MenuItem key={cat.value} value={cat.value}>
                  {cat.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <MuiTextField
            fullWidth
            multiline
            rows={4}
            label="Detailed Feedback & Guidance for Scholar"
            placeholder="e.g. Please focus on earning 20 more S.T.R.E.E.T. Cred points through on-time homework submission before reapplying..."
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            error={!rejectReason.trim()}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setIsRejectModalOpen(false)} sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1.5 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleRejectLoan}
            disabled={actionLoading || !rejectReason.trim()}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 1.5 }}
          >
            {actionLoading ? <CircularProgress size={20} color="inherit" /> : 'Confirm Decline'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 3. RECORD PAYMENT MODAL */}
      <Dialog
        open={isRecordPaymentModalOpen}
        onClose={() => !actionLoading && setIsRecordPaymentModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          Record Loan Payment
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2, fontSize: '0.8125rem' }}>
            Manually apply a payment or biweekly paycheck deduction to reduce this student's outstanding debt.
          </Typography>

          <Paper elevation={0} sx={{ p: 1.5, mb: 2, bgcolor: 'grey.50', borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                Total Remaining Balance:
              </Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                {formatScholarDollars(loanProgress?.remainingBalance || 0)}
              </Typography>
            </Stack>
          </Paper>

          <MuiTextField
            fullWidth
            size="small"
            type="number"
            label="Payment Amount ($D)"
            placeholder="e.g. 33.61"
            value={paymentAmount}
            onChange={(e) => setPaymentAmount(e.target.value)}
            sx={{ mb: 2 }}
          />

          <MuiTextField
            fullWidth
            size="small"
            label="Note / Reference"
            placeholder="e.g. Paycheck #4 auto-deduction"
            value={paymentNote}
            onChange={(e) => setPaymentNote(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setIsRecordPaymentModalOpen(false)} sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1.5 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleRecordPayment}
            disabled={actionLoading || !paymentAmount}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 1.5 }}
          >
            {actionLoading ? <CircularProgress size={20} color="inherit" /> : 'Record Payment'}
          </Button>
        </DialogActions>
      </Dialog>
    </Page>
  );
}
